import React, { useState } from 'react'
import { DurationSelect } from './UploadResumeRevision'
import { Button } from '~/lib/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { Label } from '~/lib/ui/label'
import { useServiceRequest } from './ServiceRequestDetail'
import PrivacySettings from './PrivacySettings'
import { Textarea } from '~/lib/ui/textarea'
import { CheckCircleIcon } from 'lucide-react'
import Dropzone from './Dropzone'
import { useMutation } from '@tanstack/react-query'
import { queryClient, useCandidate } from './CandidateProvider'
import { apiPatch, apiPost } from '~/util/api'
import { useToast } from '~/lib/ui/toast'
import YnsIcon from '../icons/YnsIcon'
import useServiceRequestNoteDefaults from './useServiceRequestNoteDefaults'
import RecommendedFileAttachments from './RecommendedFileAttachments'
import InitiateServiceRequestForm from './InitiateServiceRequestForm'
import { useTranslation } from '~/hooks/useTranslation'

const ServiceRequestFeedbackDialog = ({
  createRequest = false,
  detailType,
  startedAndClosed = false,
  children,
}) => {
  const currentServiceRequest = useServiceRequest()
  const { t } = useTranslation('')
  const {
    requestType,
    meetingViaCalendly,
    defaultFeedbackAttachmentKeys,
    supportingDocs,
  } = currentServiceRequest
  const [open, setOpen] = useState(false)
  const [duration, setDuration] = useState('')
  const [privacySettings, setPrivacySettings] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const allPrivacySettings = ['csm', 'coach', 'admin']
  const formPrivacySettings =
    privacySettings.indexOf('all') > -1 ? allPrivacySettings : privacySettings
  const [noteDetails, setNoteDetails] = useState('')
  const [attachmentKeys, setAttachmentKeys] = useState(
    defaultFeedbackAttachmentKeys || [],
  )
  const [formValues, setFormValues] = useState({})
  const supportedDocFiles = supportingDocs || []

  const idRef = React.useRef(null)

  const onCheckedChange = (key) => (checked) => {
    if (checked) {
      setAttachmentKeys([...attachmentKeys, key])
    } else {
      setAttachmentKeys(attachmentKeys.filter((k) => k !== key))
    }
  }

  const [selectedFiles, setSelectedFiles] = useState([])

  const { toast } = useToast()
  const { displayName } = currentServiceRequest || {}
  const { candidateId, platformName } = useCandidate()

  const updateMutation = async (formData) => {
    setIsSaving(true)
    const { id: currentServiceRequestId } = currentServiceRequest
    const idToUse = currentServiceRequestId || idRef.current
    const patchPath = `/coach/service_requests/${idToUse}.json`
    const response = await apiPatch(patchPath, formData, true)
    return response
  }

  const updateServiceRequestMutation = useMutation({
    mutationFn: updateMutation,
    onSuccess: () => {
      setIsSaving(false)
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      toast({
        title: 'Success',
        description: `${displayName} Feedback Saved`,
      })
    },
    onError: (error) => {
      setIsSaving(false)
      console.error(`Error saving ${displayName} feedback:`, error)
      toast({
        title: 'Error',
        description: `Failed to save ${displayName} feedback`,
        variant: 'destructive',
      })
    },
  })

  const createServiceRequest = async (startedAndClosed = false) => {
    if (isSaving) return

    setIsSaving(true)

    const f = new FormData()
    Object.keys(formValues).forEach((key) => {
      f.append(`service_request[${key}]`, formValues[key])
    })
    f.append('service_request[candidate_id]', candidateId)
    f.append('service_request[type]', currentServiceRequest.requestType)
    f.append('service_request[coach_started_and_closed]', startedAndClosed)

    const postPath = `/coach/service_requests.json`
    const response = await apiPost(postPath, f, true)
    if (response.status === 200) {
      idRef.current = response.data.service_request.id
      updateFeedback(true)
    } else {
      toast({
        title: 'Error',
        description: `Failed to save ${displayName} feedback`,
        variant: 'destructive',
      })
    }
  }

  const updateFeedback = async (fromCreate = false) => {
    if (isSaving && !fromCreate) return

    const f = new FormData()

    f.append('service_request[coach_note_attributes][duration]', duration)
    formPrivacySettings.forEach((setting) => {
      f.append(
        'service_request[coach_note_attributes][privacy_settings][]',
        setting,
      )
    })
    f.append('service_request[coach_note_attributes][note]', noteDetails)
    f.append('service_request[coach_message]', noteToCandidate)
    selectedFiles.forEach((file) => {
      f.append('service_request[coach_documents][]', file)
    })
    attachmentKeys.forEach((key) => {
      f.append('service_request[default_attachment_keys][]', key)
    })

    updateServiceRequestMutation.mutate(f)
  }

  const saveClick = () => {
    if (createRequest) {
      createServiceRequest(startedAndClosed)
    } else {
      updateFeedback()
    }
  }

  const removeFile = (index) => (event) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, fileIndex) => fileIndex !== index),
    )
    event.stopPropagation()
  }
  const translations = t('serviceRequestNoteDefaults')

  const body = useServiceRequestNoteDefaults({
    requestType,
    meetingViaCalendly,
    translations,
    platformName,
  })

  function getNoteDefaults(currentServiceRequest) {
    const { coachName, candidateFirstName } = currentServiceRequest
    const prefix = candidateFirstName
      ? t('serviceRequestNoteDefaults.greeting_text', { candidateFirstName })
      : ''
    const suffix = coachName
      ? t('serviceRequestNoteDefaults.coach_signature', { coachName })
      : ''
    return prefix + body + suffix
  }

  const noteDefaults = getNoteDefaults(currentServiceRequest)

  const [noteToCandidate, setNoteToCandidate] = useState(noteDefaults)

  const servicesRequiringDocuments = {
    'ServiceRequest::LinkedInReview': 'LinkedIn Review',
    'ServiceRequest::TargetCompanyResearch': 'Target Company Research',
    'ServiceRequest::CoverLetterReview': 'Cover Letter',
    'ServiceRequest::TargetedResearch': 'Targeted Research Document',
  }

  let isDocumentRequired = Object.keys(servicesRequiringDocuments).includes(
    currentServiceRequest.requestType,
  )
  if (
    currentServiceRequest.requestType === 'ServiceRequest::LinkedInReview' &&
    meetingViaCalendly
  ) {
    isDocumentRequired = false
  }

  const noDuration =
    duration === '0' || duration === 0 || duration === '' || !duration

  const formIsInvalid =
    noteToCandidate?.toUpperCase().includes('COACH TO EDIT') ||
    noteToCandidate?.trim() === undefined ||
    (isDocumentRequired && selectedFiles.length === 0) ||
    noteToCandidate?.trim() === '' ||
    noDuration

  const title = createRequest
    ? `Initiate ${displayName} and Provide Feedback`
    : `${displayName} Feedback`

  return (
    <Dialog
      open={open}
      onOpenChange={() => setOpen(!open)}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="justify-start font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">{title}</h2>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          <DialogDescription className="p-0 text-lg">
            {createRequest ? 'Initiate a service request and add ' : 'Add '}
            feedback for the candidate after the {displayName}.
          </DialogDescription>
          {createRequest && (
            <InitiateServiceRequestForm
              formValues={formValues}
              setFormValues={setFormValues}
              requestType={detailType}
            />
          )}
          <div className="py-4">
            <Label htmlFor="duration" className="text-lg !font-medium">
              Duration (Minutes) (Required)
            </Label>
            <DurationSelect onValueChange={setDuration} />
          </div>
          <div className="pb-4">
            <Label htmlFor="noteDetails" className="text-lg !font-medium">
              Internal System Admin Notes
            </Label>
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
            />
            <Textarea
              className="min-h-56 w-full"
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
            />
          </div>
          <hr className="border-t-2 border-gray-300" />
          <div className="py-4">
            <Label htmlFor="noteToCandidate" className="text-lg !font-medium">
              Please record any remarks with this update. Comments will be
              viewed by the candidate.
            </Label>
            <Textarea
              className="min-h-56 w-full"
              name="noteToCandidate"
              value={noteToCandidate}
              onChange={(e) => setNoteToCandidate(e.target.value)}
            />
          </div>
          <div className="py-4">
            {((defaultFeedbackAttachmentKeys || []).length > 0 ||
              (attachmentKeys || []).length > 0) && (
              <RecommendedFileAttachments
                defaultFeedbackAttachmentKeys={
                  defaultFeedbackAttachmentKeys || []
                }
                attachmentKeys={attachmentKeys || []}
                onCheckedChange={onCheckedChange}
              />
            )}
            <Dropzone
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              label={
                isDocumentRequired
                  ? `Upload the ${servicesRequiringDocuments[currentServiceRequest.requestType]} Here (required)`
                  : null
              }
              supportedDocFiles={supportedDocFiles}
              meetingViaCalendly={meetingViaCalendly}
            >
              {selectedFiles.length > 0 ? (
                <SelectedFilesDisplay
                  selectedFiles={selectedFiles}
                  removeFile={removeFile}
                />
              ) : (
                <p className="text-lg">
                  Drag and drop files here, or click to select files.
                </p>
              )}
            </Dropzone>
          </div>
          <div className="flex flex-row justify-end">
            <Button
              className="inline"
              onClick={saveClick}
              disabled={isSaving || formIsInvalid}
            >
              Save
            </Button>
          </div>
          <p className="mt-2 flex flex-row justify-end text-red-500">
            {noteToCandidate?.toUpperCase().includes('COACH TO EDIT') ? (
              <span>
                <b>Save Disabled:</b> Address the &quot;COACH TO EDIT&quot; text
                in your note to the candidate.
              </span>
            ) : noteToCandidate?.trim() === undefined ||
              noteToCandidate?.trim() === '' ? (
              <span>
                <b>Save Disabled:</b> Note to candidate is required.
              </span>
            ) : isDocumentRequired && selectedFiles.length === 0 ? (
              <span>
                <b>Save Disabled:</b> Please upload a document.
              </span>
            ) : noDuration ? (
              <span>
                <b>Save Disabled:</b> Please select a duration.
              </span>
            ) : (
              ''
            )}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default ServiceRequestFeedbackDialog

const SelectedFilesDisplay = ({ selectedFiles, removeFile }) => {
  return selectedFiles.map((file, index) => (
    <div
      className="mb-4 flex flex-row justify-center"
      key={`${file.name}-${index}`}
    >
      <CheckCircleIcon className="mr-2 inline w-6 text-intoo-green-coachcta" />
      {`${file.name} (${(file.size / 1024).toFixed(2)} KB) ${file.type ? ` (${file.type})` : ''}`}
      <Button
        variant="link"
        className="ml-2 text-red-900"
        title="Remove this file"
        onClick={removeFile(index)}
      >
        <YnsIcon name="trash" className="h-6 w-6" />
      </Button>
    </div>
  ))
}
